import type { DailyHistoryPoint } from "./queries";
import type { ReportingPlatform } from "./reportingConfig";

export type Granularity = "day" | "week" | "month";
export type RangePreset = "1m" | "3m" | "6m" | "ytd" | "1y" | "2y" | "all";

export const MONTHS_ES = [
  "Ene", "Feb", "Mar", "Abr", "May", "Jun",
  "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
];

export interface AggPoint {
  key: string;
  label: string;
  fullLabel: string;
  active: number | null;
  activeMin: number | null;
  activeMax: number | null;
  altas: number | null;
  bajas: number | null;
  effectiveness: number | null;
  effectivenessMin: number | null;
  effectivenessMax: number | null;
  effectivenessAvg: number | null;
  effectivenessPeak: number | null;
  reporting: number | null;
  total: number | null;
  hasData: boolean;
  days: number;
  monthStart: boolean;
}

interface EffStat {
  eff: number;
  reporting: number;
  notReporting: number;
}

const statOf = (
  r: DailyHistoryPoint,
  platform?: ReportingPlatform,
): EffStat => {
  if (!platform) {
    return {
      eff: r.effectiveness_pct,
      reporting: r.reporting,
      notReporting: r.not_reporting,
    };
  }
  const p = r.by_platform?.[platform];
  return {
    eff: p?.effectiveness_pct ?? 0,
    reporting: p?.reporting ?? 0,
    notReporting: p?.not_reporting ?? 0,
  };
};

const round1 = (n: number): number => Math.round(n * 10) / 10;

const parseISO = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
};

const toISO = (d: Date): string =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate(),
  ).padStart(2, "0")}`;

const monthKey = (d: Date): string =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;

const isoWeekKey = (d: Date): string => {
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - dow);
  return toISO(t);
};

const fmtLong = (iso: string): string => {
  const d = parseISO(iso);
  return `${d.getUTCDate()} ${MONTHS_ES[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

export const todayMxISO = (): string => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "01";
  return `${get("year")}-${get("month")}-${get("day")}`;
};

export const rangeToWindow = (
  preset: RangePreset,
  minISO: string,
  maxISO: string,
): { from: string; to: string } => {
  const clamp = (iso: string) => (iso < minISO ? minISO : iso);
  if (preset === "all") return { from: minISO, to: maxISO };
  const end = parseISO(maxISO);
  if (preset === "ytd") {
    return { from: clamp(`${end.getUTCFullYear()}-01-01`), to: maxISO };
  }
  const months: Record<string, number> = { "1m": 1, "3m": 3, "6m": 6 };
  const years: Record<string, number> = { "1y": 1, "2y": 2 };
  const start = new Date(end);
  if (preset in months) start.setUTCMonth(end.getUTCMonth() - months[preset]);
  else if (preset in years) start.setUTCFullYear(end.getUTCFullYear() - years[preset]);
  return { from: clamp(toISO(start)), to: maxISO };
};

export const autoGranularity = (spanDays: number): Granularity => {
  if (spanDays <= 62) return "day";
  if (spanDays <= 420) return "week";
  return "month";
};

export const spanInDays = (fromISO: string, toISO2: string): number =>
  Math.round((parseISO(toISO2).getTime() - parseISO(fromISO).getTime()) / 86400000);

export const aggWord = (gran: Granularity, kind: "level" | "flow"): string => {
  if (gran === "day") return "Diario";
  const per = gran === "week" ? "sem." : "mes";
  return `${kind === "level" ? "Promedio" : "Suma"} / ${per}`;
};

const altasOf = (r: DailyHistoryPoint, platform?: ReportingPlatform): number =>
  platform ? (r.altas_by_platform?.[platform] ?? 0) : r.altas_count;

const bajasOf = (r: DailyHistoryPoint, platform?: ReportingPlatform): number =>
  platform ? (r.bajas_by_platform?.[platform] ?? 0) : r.bajas_count;

const dayPoint = (
  r: DailyHistoryPoint,
  prev: DailyHistoryPoint | null,
  platform?: ReportingPlatform,
): AggPoint => {
  const has = r.has_data !== false;
  const d = parseISO(r.date);
  const prevMonth = prev ? parseISO(prev.date).getUTCMonth() : -1;
  const stat = statOf(r, platform);
  return {
    key: r.date,
    label: `${d.getUTCDate()} ${MONTHS_ES[d.getUTCMonth()]}`,
    fullLabel: fmtLong(r.date),
    active: has ? r.active_units : null,
    activeMin: null,
    activeMax: null,
    altas: has ? altasOf(r, platform) : null,
    bajas: has ? bajasOf(r, platform) : null,
    effectiveness: has ? stat.eff : null,
    effectivenessMin: null,
    effectivenessMax: null,
    effectivenessAvg: has && !platform ? r.effectiveness_avg_pct : null,
    effectivenessPeak: has && !platform ? r.effectiveness_peak_pct : null,
    reporting: has ? stat.reporting : null,
    total: has ? stat.reporting + stat.notReporting : null,
    hasData: has,
    days: 1,
    monthStart: d.getUTCMonth() !== prevMonth,
  };
};

const groupPoint = (
  key: string,
  rows: DailyHistoryPoint[],
  gran: Granularity,
  prevKey: string | null,
  platform?: ReportingPlatform,
): AggPoint => {
  const withData = rows.filter((r) => r.has_data !== false);
  const actives = withData.map((r) => r.active_units);
  const effs = withData.map((r) => statOf(r, platform).eff);
  const mid = parseISO(rows[Math.floor(rows.length / 2)].date);
  const avg = actives.length
    ? Math.round(actives.reduce((a, b) => a + b, 0) / actives.length)
    : null;
  const effAvg = effs.length
    ? round1(effs.reduce((a, b) => a + b, 0) / effs.length)
    : null;
  const monthOfKey = parseISO(key).getUTCMonth();
  const prevMonth = prevKey ? parseISO(prevKey).getUTCMonth() : -1;
  return {
    key,
    label:
      gran === "month"
        ? `${MONTHS_ES[mid.getUTCMonth()]} ${String(mid.getUTCFullYear()).slice(2)}`
        : `${mid.getUTCDate()} ${MONTHS_ES[mid.getUTCMonth()]}`,
    fullLabel:
      gran === "month"
        ? `${MONTHS_ES[mid.getUTCMonth()]} ${mid.getUTCFullYear()}`
        : `Semana del ${fmtLong(key)}`,
    active: avg,
    activeMin: actives.length ? Math.min(...actives) : null,
    activeMax: actives.length ? Math.max(...actives) : null,
    altas: withData.length ? withData.reduce((a, r) => a + altasOf(r, platform), 0) : null,
    bajas: withData.length ? withData.reduce((a, r) => a + bajasOf(r, platform), 0) : null,
    effectiveness: effAvg,
    effectivenessMin: effs.length ? round1(Math.min(...effs)) : null,
    effectivenessMax: effs.length ? round1(Math.max(...effs)) : null,
    effectivenessAvg: null,
    effectivenessPeak: null,
    reporting: null,
    total: null,
    hasData: withData.length > 0,
    days: rows.length,
    monthStart: gran === "week" && monthOfKey !== prevMonth,
  };
};

export const aggregate = (
  rows: DailyHistoryPoint[],
  gran: Granularity,
  platform?: ReportingPlatform,
): AggPoint[] => {
  if (gran === "day") {
    return rows.map((r, i) => dayPoint(r, i > 0 ? rows[i - 1] : null, platform));
  }
  const groups = new Map<string, DailyHistoryPoint[]>();
  rows.forEach((r) => {
    const d = parseISO(r.date);
    const key = gran === "week" ? isoWeekKey(d) : monthKey(d);
    const arr = groups.get(key) ?? [];
    arr.push(r);
    groups.set(key, arr);
  });
  const keys = [...groups.keys()].sort();
  return keys.map((key, i) =>
    groupPoint(key, groups.get(key)!, gran, i > 0 ? keys[i - 1] : null, platform),
  );
};
